import {
  Container,
  Graphics,
  Rectangle,
  Text,
  type FederatedPointerEvent,
  type FederatedWheelEvent
} from 'pixi.js';
import { BaseScene, type SceneServices } from './BaseScene';
import type { GameConfig } from '../app/GameConfig';
import type { CaseDefinition, CaseStatus } from '../app/types';
import type { ViewportLayout } from '../app/Viewport';
import casesData from '../data/cases.json';
import {
  Button,
  HeaderBar,
  Modal,
  Panel,
  UI_COLORS,
  UI_FONT_FAMILY,
  createTextLayoutDebugEntry,
  fitTextToBounds,
  type TextLayoutDebugState
} from '../ui';

export interface CaseListSceneOptions {
  onBack?: () => void;
  onStartCase?: (caseDefinition: CaseDefinition) => void;
  onOpenEvidence?: () => void;
  onOpenSettings?: () => void;
  onGoToTitle?: () => void;
}

interface CaseRow {
  container: Container;
  background: Graphics;
  caseDefinition: CaseDefinition;
  status: CaseStatus;
  titleText: Text;
  statusText: Text;
}

const CASES = [...(casesData as { cases: CaseDefinition[] }).cases].sort(
  (left, right) => left.displayOrder - right.displayOrder
);
const LIST_WIDTH = 430;
const DETAIL_WIDTH = 670;
const PANEL_HEIGHT = 560;
const ROW_WIDTH = LIST_WIDTH - 60;
const ROW_HEIGHT = 92;
const ROW_GAP = 14;
const ROW_TEXT_WIDTH = ROW_WIDTH - 36;
const ROW_TITLE_HEIGHT = 42;
const LIST_VIEWPORT_HEIGHT = PANEL_HEIGHT - 112;
const LIST_SCROLLBAR_X = ROW_WIDTH + 12;
const LIST_SCROLLBAR_WIDTH = 8;
const LIST_SCROLL_STEP = ROW_HEIGHT + ROW_GAP;
const LIST_DRAG_THRESHOLD = 4;
const LIST_WHEEL_SCROLL_MULTIPLIER = 0.5;
const LIST_WHEEL_MIN_DISTANCE = 24;
const LIST_WHEEL_MAX_DISTANCE = 80;
const DETAIL_TEXT_WIDTH = DETAIL_WIDTH - 72;
const DETAIL_TITLE_HEIGHT = 46;
const DETAIL_SUBTITLE_HEIGHT = 42;
const DETAIL_SUMMARY_HEIGHT = 280;
const DETAIL_META_HEIGHT = 82;

export class CaseListScene extends BaseScene {
  private readonly background = new Graphics();
  private readonly titleText = new Text({
    text: '未解決事件リスト',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 34,
      fontWeight: '900'
    }
  });
  private readonly subtitleText = new Text({
    text: '迷華町で増え続ける事件ファイル',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '700'
    }
  });
  private readonly headerBar = new HeaderBar({
    width: 520,
    disabledActions: ['caseList'],
    onAction: (action) => this.handleHeaderAction(action)
  });
  private readonly startCaseButton = new Button({
    label: 'この事件を開始',
    width: 220,
    height: 50,
    variant: 'primary',
    onPress: () => this.handleStartSelectedCase()
  });
  private readonly listPanel = new Panel({
    width: LIST_WIDTH,
    height: PANEL_HEIGHT,
    title: '事件一覧'
  });
  private readonly detailPanel = new Panel({
    width: DETAIL_WIDTH,
    height: PANEL_HEIGHT,
    title: '事件詳細'
  });
  private readonly listViewport = new Container();
  private readonly rowLayer = new Container();
  private readonly listMask = new Graphics();
  private readonly scrollTrack = new Graphics();
  private readonly scrollThumb = new Graphics();
  private readonly detailTitleText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 29,
      fontWeight: '900',
      wordWrap: true,
      wordWrapWidth: DETAIL_WIDTH - 72
    }
  });
  private readonly detailSubtitleText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '800',
      wordWrap: true,
      wordWrapWidth: DETAIL_WIDTH - 72
    }
  });
  private readonly detailSummaryText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      lineHeight: 34,
      wordWrap: true,
      wordWrapWidth: DETAIL_WIDTH - 72
    }
  });
  private readonly detailMetaText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 16,
      lineHeight: 24,
      wordWrap: true,
      wordWrapWidth: DETAIL_WIDTH - 72
    }
  });
  private readonly stampText = new Text({
    text: '未解決',
    style: {
      fill: UI_COLORS.red,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 88,
      fontWeight: '900'
    }
  });
  private readonly modalLayer = new Container();
  private readonly rows: CaseRow[] = [];
  private layout: ViewportLayout | null = null;
  private noticeModal: Modal | null = null;
  private selectedCaseId: string | null = null;
  private listScrollOffset = 0;
  private listDragActive = false;
  private listDragMoved = false;
  private listDragStartY = 0;
  private listDragStartOffset = 0;

  public constructor(
    services: SceneServices,
    private readonly config: GameConfig,
    private readonly options: CaseListSceneOptions = {}
  ) {
    super(services);
    this.stampText.anchor.set(0.5);
    this.stampText.rotation = -0.13;
  }

  protected override onStart(): void {
    this.container.addChild(this.background);
    this.container.addChild(this.titleText);
    this.container.addChild(this.subtitleText);
    this.container.addChild(this.headerBar);
    this.container.addChild(this.listPanel);
    this.container.addChild(this.detailPanel);
    this.container.addChild(this.modalLayer);
    this.listPanel.content.addChild(this.listViewport);
    this.listPanel.content.addChild(this.listMask);
    this.listPanel.content.addChild(this.scrollTrack);
    this.listPanel.content.addChild(this.scrollThumb);
    this.listViewport.addChild(this.rowLayer);
    this.listViewport.mask = this.listMask;
    this.listViewport.eventMode = 'static';
    this.listViewport.cursor = 'default';
    this.listViewport.hitArea = new Rectangle(0, 0, ROW_WIDTH, LIST_VIEWPORT_HEIGHT);
    this.listViewport.on('wheel', this.handleListWheel);
    this.listViewport.on('pointerdown', this.handleListPointerDown);
    this.listViewport.on('pointermove', this.handleListPointerMove);
    this.listViewport.on('pointerup', this.handleListPointerUp);
    this.listViewport.on('pointerupoutside', this.handleListPointerUp);
    this.detailPanel.content.addChild(this.detailTitleText);
    this.detailPanel.content.addChild(this.detailSubtitleText);
    this.detailPanel.content.addChild(this.detailSummaryText);
    this.detailPanel.content.addChild(this.detailMetaText);
    this.detailPanel.content.addChild(this.stampText);
    this.detailPanel.content.addChild(this.startCaseButton);
    this.renderRows();
    this.selectCase(this.getDefaultSelectedCaseId());
    this.resize(this.services.getLayout());
  }

  protected override onDestroy(): void {
    this.destroyNoticeModal();
  }

  public override resize(layout: ViewportLayout): void {
    this.layout = layout;
    this.drawBackground(layout.baseWidth, layout.baseHeight);
    this.titleText.position.set(layout.safeArea.x, layout.safeArea.y + 16);
    this.subtitleText.position.set(layout.safeArea.x + 4, layout.safeArea.y + 60);
    this.headerBar.position.set(layout.safeArea.x + layout.safeArea.width - 520, layout.safeArea.y);
    this.listPanel.position.set(layout.safeArea.x, 120);
    this.detailPanel.position.set(layout.safeArea.x + LIST_WIDTH + 34, 120);
    this.layoutRows();
    this.renderSelectedDetail();
    this.noticeModal?.setViewportSize(layout.baseWidth, layout.baseHeight);
  }

  public override getTextLayoutDebugState(): TextLayoutDebugState {
    return {
      scene: 'caseList',
      entries: [
        ...this.rows.flatMap((row) => [
          createTextLayoutDebugEntry(
            `caseList.row.${row.caseDefinition.id}.title`,
            row.titleText,
            ROW_TEXT_WIDTH,
            ROW_TITLE_HEIGHT
          ),
          createTextLayoutDebugEntry(
            `caseList.row.${row.caseDefinition.id}.status`,
            row.statusText,
            ROW_TEXT_WIDTH,
            24
          )
        ]),
        createTextLayoutDebugEntry(
          'caseList.detail.title',
          this.detailTitleText,
          DETAIL_TEXT_WIDTH,
          DETAIL_TITLE_HEIGHT
        ),
        createTextLayoutDebugEntry(
          'caseList.detail.subtitle',
          this.detailSubtitleText,
          DETAIL_TEXT_WIDTH,
          DETAIL_SUBTITLE_HEIGHT
        ),
        createTextLayoutDebugEntry(
          'caseList.detail.summary',
          this.detailSummaryText,
          DETAIL_TEXT_WIDTH,
          DETAIL_SUMMARY_HEIGHT
        ),
        createTextLayoutDebugEntry(
          'caseList.detail.meta',
          this.detailMetaText,
          DETAIL_TEXT_WIDTH,
          DETAIL_META_HEIGHT
        )
      ]
    };
  }

  private renderRows(): void {
    destroyRows(this.rows);
    this.rowLayer.removeChildren();

    for (const caseDefinition of CASES) {
      const status = this.getCaseStatus(caseDefinition);
      const row = this.createCaseRow(caseDefinition, status);
      this.rows.push(row);
      this.rowLayer.addChild(row.container);
    }
  }

  private createCaseRow(caseDefinition: CaseDefinition, status: CaseStatus): CaseRow {
    const container = new Container();
    const background = new Graphics();
    const titleText = new Text({
      text: getCaseDisplayTitle(caseDefinition, status),
      style: {
        fill: status === 'hidden' ? UI_COLORS.mutedInk : UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 20,
        fontWeight: '800',
        wordWrap: true,
        wordWrapWidth: LIST_WIDTH - 88
      }
    });
    const statusText = new Text({
      text: getCaseStatusLabel(status),
      style: {
        fill: getCaseStatusColor(status),
        fontFamily: UI_FONT_FAMILY,
        fontSize: 15,
        fontWeight: '800'
      }
    });

    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', (event: FederatedPointerEvent) => {
      event.stopPropagation();
      if (this.listDragMoved) {
        return;
      }

      this.selectCase(caseDefinition.id);
    });
    container.addChild(background);
    container.addChild(titleText);
    container.addChild(statusText);

    return {
      container,
      background,
      caseDefinition,
      status,
      titleText,
      statusText
    };
  }

  private layoutRows(): void {
    for (const [index, row] of this.rows.entries()) {
      const y = index * LIST_SCROLL_STEP;
      const selected = row.caseDefinition.id === this.selectedCaseId;
      row.container.position.set(0, y);
      row.background.clear();
      row.background
        .roundRect(0, 0, ROW_WIDTH, ROW_HEIGHT, 6)
        .fill({
          color: selected ? UI_COLORS.redDark : UI_COLORS.paperLight,
          alpha: selected ? 0.92 : 0.76
        })
        .stroke({
          color: getCaseStatusColor(row.status),
          width: 2,
          alpha: selected ? 0.92 : 0.46
        });
      row.titleText.position.set(18, 14);
      row.statusText.position.set(18, 60);
      fitTextToBounds(row.titleText, {
        maxWidth: ROW_TEXT_WIDTH,
        maxHeight: ROW_TITLE_HEIGHT,
        baseFontSize: 20,
        minFontSize: 14,
        baseLineHeight: 24,
        minLineHeight: 18,
        wordWrap: true,
        breakWords: true
      });
      fitTextToBounds(row.statusText, {
        maxWidth: ROW_TEXT_WIDTH,
        maxHeight: 24,
        baseFontSize: 15,
        minFontSize: 11,
        baseLineHeight: 18,
        minLineHeight: 14,
        wordWrap: false,
        breakWords: false
      });
    }

    this.drawListViewport();
    this.setListScrollOffset(this.listScrollOffset);
  }

  private selectCase(caseId: string | null): void {
    this.selectedCaseId = caseId;
    this.layoutRows();
    this.renderSelectedDetail();
  }

  private renderSelectedDetail(): void {
    const caseDefinition = CASES.find((item) => item.id === this.selectedCaseId) ?? CASES[0];
    if (!caseDefinition) {
      this.detailTitleText.text = '事件なし';
      this.detailSubtitleText.text = '';
      this.detailSummaryText.text = 'まだ表示できる事件ファイルはありません。';
      this.detailMetaText.text = '';
      this.stampText.visible = false;
      return;
    }

    const status = this.getCaseStatus(caseDefinition);
    this.detailTitleText.text = getCaseDisplayTitle(caseDefinition, status);
    this.detailSubtitleText.text = getCaseDetailSubtitle(caseDefinition, status);
    this.detailSummaryText.text = getCaseDetailText(caseDefinition, status);
    this.detailMetaText.text = getCaseMetaText(caseDefinition, status);
    this.stampText.visible = shouldShowUnresolvedStamp(status);

    this.detailTitleText.position.set(8, 0);
    this.detailSubtitleText.position.set(8, 48);
    this.detailSummaryText.position.set(8, 96);
    this.detailMetaText.position.set(8, 396);
    this.stampText.position.set(DETAIL_WIDTH - 190, 320);
    this.startCaseButton.position.set(DETAIL_WIDTH - 292, PANEL_HEIGHT - 114);
    this.startCaseButton.setDisabled(status === 'hidden' || !this.options.onStartCase);
    this.fitDetailText();
  }

  private fitDetailText(): void {
    fitTextToBounds(this.detailTitleText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_TITLE_HEIGHT,
      baseFontSize: 29,
      minFontSize: 18,
      baseLineHeight: 35,
      minLineHeight: 24,
      wordWrap: true,
      breakWords: true
    });
    fitTextToBounds(this.detailSubtitleText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_SUBTITLE_HEIGHT,
      baseFontSize: 18,
      minFontSize: 13,
      baseLineHeight: 22,
      minLineHeight: 16,
      wordWrap: true,
      breakWords: true
    });
    fitTextToBounds(this.detailSummaryText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_SUMMARY_HEIGHT,
      baseFontSize: 22,
      minFontSize: 16,
      baseLineHeight: 34,
      minLineHeight: 23,
      wordWrap: true,
      breakWords: true
    });
    fitTextToBounds(this.detailMetaText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_META_HEIGHT,
      baseFontSize: 16,
      minFontSize: 12,
      baseLineHeight: 24,
      minLineHeight: 16,
      wordWrap: true,
      breakWords: true
    });
  }

  private getDefaultSelectedCaseId(): string | null {
    return (
      CASES.find((caseDefinition) => this.getCaseStatus(caseDefinition) !== 'hidden')?.id ??
      CASES[0]?.id ??
      null
    );
  }

  private getCaseStatus(caseDefinition: CaseDefinition): CaseStatus {
    return this.services.getState().getCaseStatus(caseDefinition.id) ?? caseDefinition.status;
  }

  private handleHeaderAction(
    action: 'save' | 'evidence' | 'caseList' | 'settings' | 'title'
  ): void {
    switch (action) {
      case 'save':
      case 'caseList':
        this.headerBar.releaseActionLock();
        break;
      case 'evidence':
        if (this.options.onOpenEvidence) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenEvidence?.());
        } else {
          this.showNotice(
            '証拠品',
            'いまは証拠品画面を開けません。タイトルから再度お試しください。'
          );
        }
        break;
      case 'settings':
        if (this.options.onOpenSettings) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenSettings?.());
        } else {
          this.showNotice('設定', 'いまは設定画面を開けません。タイトルから再度お試しください。');
        }
        break;
	      case 'title':
	        this.navigateAfterCurrentPointer(() => this.options.onBack?.());
	        break;
	    }
	  }

  private handleStartSelectedCase(): void {
    const caseDefinition = CASES.find((item) => item.id === this.selectedCaseId);
    if (!caseDefinition) {
      return;
    }

    const status = this.getCaseStatus(caseDefinition);
    if (status === 'hidden') {
      this.showNotice('事件未発生', 'この事件ファイルはまだ開けません。');
      return;
    }

    this.navigateAfterCurrentPointer(() => this.options.onStartCase?.(caseDefinition));
  }

  private navigateAfterCurrentPointer(navigate: () => void): void {
    this.services.getInput().lock();
    try {
      navigate();
    } finally {
      window.setTimeout(() => this.services.getInput().unlock(), 0);
    }
  }

  private showNotice(title: string, message: string): void {
    this.destroyNoticeModal();
    this.services.getInput().lock();
    const layout = this.layout;
    const modal = new Modal({
      title,
      message,
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: '閉じる',
          variant: 'primary',
          onPress: () => this.destroyNoticeModal()
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });

    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private destroyNoticeModal(): void {
    if (!this.noticeModal) {
      return;
    }

    this.noticeModal.destroy({ children: true });
    this.noticeModal = null;
    this.services.getInput().unlock();
    this.headerBar.releaseActionLock();
  }

  private drawBackground(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height).fill(UI_COLORS.black);
    this.background.rect(0, 0, width, height).fill({ color: UI_COLORS.redDark, alpha: 0.22 });
    this.background.rect(0, 0, width, 8).fill({ color: UI_COLORS.red, alpha: 0.84 });
    this.background.rect(0, height - 8, width, 8).fill({ color: UI_COLORS.gold, alpha: 0.72 });
  }

  private readonly handleListWheel = (event: FederatedWheelEvent): void => {
    event.stopPropagation();
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (delta === 0) {
      return;
    }

    const distance =
      Math.sign(delta) *
      Math.min(
        LIST_WHEEL_MAX_DISTANCE,
        Math.max(LIST_WHEEL_MIN_DISTANCE, Math.abs(delta) * LIST_WHEEL_SCROLL_MULTIPLIER)
      );
    this.setListScrollOffset(this.listScrollOffset + distance);
  };

  private readonly handleListPointerDown = (event: FederatedPointerEvent): void => {
    event.stopPropagation();
    this.listDragActive = this.getMaxListScrollOffset() > 0;
    this.listDragMoved = false;
    this.listDragStartY = event.global.y;
    this.listDragStartOffset = this.listScrollOffset;
    this.listViewport.cursor = this.listDragActive ? 'grabbing' : 'default';
  };

  private readonly handleListPointerMove = (event: FederatedPointerEvent): void => {
    if (!this.listDragActive) {
      return;
    }

    event.stopPropagation();
    const deltaY = event.global.y - this.listDragStartY;
    if (Math.abs(deltaY) >= LIST_DRAG_THRESHOLD) {
      this.listDragMoved = true;
    }
    this.setListScrollOffset(this.listDragStartOffset - deltaY);
  };

  private readonly handleListPointerUp = (event: FederatedPointerEvent): void => {
    if (!this.listDragActive && !this.listDragMoved) {
      return;
    }

    event.stopPropagation();
    this.listDragActive = false;
    this.listViewport.cursor = this.getMaxListScrollOffset() > 0 ? 'grab' : 'default';
    window.setTimeout(() => {
      this.listDragMoved = false;
    }, 0);
  };

  private drawListViewport(): void {
    this.listMask.clear();
    this.listMask.rect(0, 0, ROW_WIDTH, LIST_VIEWPORT_HEIGHT).fill(UI_COLORS.ink);

    const maxScrollOffset = this.getMaxListScrollOffset();
    const scrollbarVisible = maxScrollOffset > 0;
    this.scrollTrack.visible = scrollbarVisible;
    this.scrollThumb.visible = scrollbarVisible;
    this.listViewport.cursor = getListViewportCursor(scrollbarVisible, this.listDragActive);

    this.scrollTrack.clear();
    this.scrollThumb.clear();

    if (!scrollbarVisible) {
      return;
    }

    this.scrollTrack
      .roundRect(LIST_SCROLLBAR_X, 0, LIST_SCROLLBAR_WIDTH, LIST_VIEWPORT_HEIGHT, 4)
      .fill({ color: UI_COLORS.black, alpha: 0.42 })
      .stroke({ color: UI_COLORS.gold, width: 1, alpha: 0.36 });
    this.drawScrollThumb();
  }

  private drawScrollThumb(): void {
    const maxScrollOffset = this.getMaxListScrollOffset();
    this.scrollThumb.clear();
    if (maxScrollOffset <= 0) {
      return;
    }

    const contentHeight = this.getListContentHeight();
    const thumbHeight = Math.max(
      64,
      Math.min(LIST_VIEWPORT_HEIGHT, (LIST_VIEWPORT_HEIGHT / contentHeight) * LIST_VIEWPORT_HEIGHT)
    );
    const thumbTravel = Math.max(0, LIST_VIEWPORT_HEIGHT - thumbHeight);
    const thumbY = (this.listScrollOffset / maxScrollOffset) * thumbTravel;
    this.scrollThumb
      .roundRect(LIST_SCROLLBAR_X + 1, thumbY, LIST_SCROLLBAR_WIDTH - 2, thumbHeight, 4)
      .fill({ color: UI_COLORS.gold, alpha: 0.82 });
  }

  private setListScrollOffset(offset: number): void {
    const nextOffset = Math.max(0, Math.min(offset, this.getMaxListScrollOffset()));
    this.listScrollOffset = nextOffset;
    this.rowLayer.position.set(0, -nextOffset);
    this.updateRowInteraction();
    this.drawScrollThumb();
  }

  private updateRowInteraction(): void {
    for (const row of this.rows) {
      const y = row.container.y - this.listScrollOffset;
      const visible = y + ROW_HEIGHT > 0 && y < LIST_VIEWPORT_HEIGHT;
      row.container.eventMode = visible ? 'static' : 'none';
      row.container.cursor = visible ? 'pointer' : 'default';
    }
  }

  private getListContentHeight(): number {
    if (this.rows.length === 0) {
      return 0;
    }

    return this.rows.length * ROW_HEIGHT + (this.rows.length - 1) * ROW_GAP;
  }

  private getMaxListScrollOffset(): number {
    return Math.max(0, this.getListContentHeight() - LIST_VIEWPORT_HEIGHT);
  }
}

function getListViewportCursor(scrollbarVisible: boolean, dragging: boolean): string {
  if (!scrollbarVisible) {
    return 'default';
  }

  return dragging ? 'grabbing' : 'grab';
}

function getCaseDisplayTitle(caseDefinition: CaseDefinition, status: CaseStatus): string {
  return status === 'hidden'
    ? '未発生の事件'
    : `Case ${caseDefinition.displayOrder}: ${caseDefinition.title}`;
}

function getCaseDetailText(caseDefinition: CaseDefinition, status: CaseStatus): string {
  if (status === 'hidden') {
    return 'まだ事件ファイルは作成されていません。住民は今日も、防犯ブザーの電池残量だけを気にしています。';
  }

  if (status === 'truth_suspected') {
    return `${caseDefinition.summary}\n\n${caseDefinition.unresolvedLog ?? ''}\n\n${caseDefinition.truthHint ?? '灯真は真相の輪郭を察しているが、口に出せる証拠はない。'}`.trim();
  }

  if (status === 'unresolved' || status === 'misdirected') {
    return `${caseDefinition.summary}\n\n${caseDefinition.unresolvedLog ?? '事件は未解決のまま記録された。'}`.trim();
  }

  return `${caseDefinition.summary}\n\n調査は継続中。未解決事件ファイルとして扱う。`;
}

function getCaseDetailSubtitle(caseDefinition: CaseDefinition, status: CaseStatus): string {
  if (status === 'hidden') {
    return `未発生の事件ファイル / ${getCaseStatusLabel(status)}`;
  }

  return `${caseDefinition.subtitle ?? '事件ファイル'} / ${getCaseStatusLabel(status)}`;
}

function getCaseMetaText(caseDefinition: CaseDefinition, status: CaseStatus): string {
  if (status === 'hidden') {
    return '公開状態: 未発生\n事件ファイルはまだ作成されていません。';
  }

  return [
    `公開状態: ${getCaseStatusLabel(status)}`,
    `手がかり: ${caseDefinition.evidenceIds.length}件`,
    `結末: ${getCaseEndingLabel(caseDefinition.endingType)}`
  ].join('\n');
}

function getCaseStatusLabel(status: CaseStatus): string {
  switch (status) {
    case 'hidden':
      return '未発生';
    case 'investigating':
      return '調査中';
    case 'truth_suspected':
      return '未解決 / 真相推測';
    case 'unresolved':
      return '未解決';
    case 'misdirected':
      return '迷宮入り';
  }
}

function getCaseStatusColor(status: CaseStatus): number {
  switch (status) {
    case 'hidden':
      return UI_COLORS.disabled;
    case 'investigating':
      return UI_COLORS.gold;
    case 'truth_suspected':
    case 'unresolved':
    case 'misdirected':
      return UI_COLORS.red;
  }
}

function getCaseEndingLabel(endingType: CaseDefinition['endingType']): string {
  switch (endingType) {
    case 'truth_suspected':
      return '真相推測のまま未解決';
    case 'unresolved':
      return '未解決';
    case 'misdirected':
      return '迷宮入り';
    case 'false_accusation_prevented':
      return '誤認逮捕を防止';
    case 'damage_contained':
      return '被害拡大を阻止';
    case 'damage_expanded':
      return '被害拡大';
    case undefined:
      return '記録なし';
  }
}

function shouldShowUnresolvedStamp(status: CaseStatus): boolean {
  return status === 'truth_suspected' || status === 'unresolved' || status === 'misdirected';
}

function destroyRows(rows: CaseRow[]): void {
  for (const row of rows.splice(0)) {
    row.container.destroy({ children: true });
  }
}
